/**
 * New console — reading an API response.
 *
 * `formatApiResponse` SPREADS the response body into its result rather than nesting
 * it. So a list endpoint answering
 *
 *     { page, limit, total, data: [ ...rows ] }
 *
 * arrives as `{ isOk, page, limit, total, data: [...], status, error }` — the rows
 * are `res.data` and the count is `res.total`. Reaching for `res.data.data` returns
 * undefined, and a screen written that way renders an empty table against a healthy
 * 200. That is exactly what happened to every screen in this folder once, silently,
 * because an empty list is indistinguishable from a screen with nothing to show.
 *
 * Every screen goes through these three functions so there is one place that knows
 * the shape, and so the mistake cannot be made one file at a time.
 */

/** Rows from a list endpoint. Always an array, so `.map` is safe on a failure. */
export const rowsOf = (res) => (res && res.isOk && Array.isArray(res.data) ? res.data : []);

/** Total across all pages — not the length of the page in hand. */
export const totalOf = (res) => (res && res.isOk && res.total ? res.total : 0);

/**
 * The body of an endpoint that answers with an object or a string rather than a
 * list: a dashboard, a report, a single record, a message.
 */
export const bodyOf = (res, fallback = null) => (res && res.isOk && res.data !== undefined ? res.data : fallback);

/**
 * The body of an endpoint that returns its fields at the TOP level rather than under
 * `data` — /calls/dashboard is the one that does this, answering with a flat map of
 * tile name to count. Once formatApiResponse has spread it, those counts sit beside
 * the wrapper's own keys, so the wrapper keys are what has to come off.
 *
 * Reaching for `res.data` on such a response returns undefined, which is how the
 * dashboard came to render every tile as zero against a perfectly healthy 200.
 */
const WRAPPER_KEYS = ['isOk', 'status', 'error', 'page', 'limit', 'isLast', 'next', 'prev', 'total'];

export const flatBodyOf = (res) => {
  if (!res || !res.isOk) return {};
  return Object.keys(res).reduce(
    (out, k) => (WRAPPER_KEYS.indexOf(k) >= 0 ? out : Object.assign(out, { [k]: res[k] })),
    {}
  );
};

/**
 * Reading a rejection.
 *
 * DRF answers a failed save with an OBJECT keyed by field — `{email: ["already in
 * use"], timezone: ["required"]}` — not a sentence. A screen that renders that as one
 * flat "could not save" tells someone their eight-field form failed without saying
 * which field, which is the same as not telling them. Every save in this folder goes
 * through these two so the message lands on the field that caused it.
 *
 * Returns `{}` when the error is not per-field, so a caller can fall through to
 * `errorText` for the sentence form.
 */
export const fieldErrors = (res) => {
  const detail = res && res.error;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return {};
  return Object.keys(detail).reduce((out, k) => Object.assign(out, {
    [k]: Array.isArray(detail[k]) ? String(detail[k][0]) : String(detail[k])
  }), {});
};

/** The sentence form, for a rejection that names no field. A gateway's HTML error page
    is a string too, and is not a sentence anyone should be shown. */
export const errorText = (res, fallback) => {
  const detail = res && res.error;
  if (typeof detail === 'string' && detail.trim() && !/^\s*</.test(detail)) return detail;
  if (detail && typeof detail === 'object' && typeof detail.detail === 'string') return detail.detail;
  return fallback;
};

/**
 * Whether a value the API sent is actually set. An empty key comes back as the four
 * characters "None" — Python's None through a string column — rather than as null,
 * and treating that as truthy once marked every clinic fully integrated. Four files
 * each had their own copy of this line; this is the one.
 */
export const isSet = (v) => !!v && String(v).trim() !== '' && String(v).trim() !== 'None';
